import React, { ReactNode, useCallback, useMemo, useState } from "react";
import { Link, Redirect, useParams } from "react-router-dom";

import AutocompleteSelectInput from "veneer/AutocompleteSelectInput";
import Box from "veneer/Box";
import Button from "veneer/Button";
import { JollyRancherChip } from "veneer/Chip";
import DescriptionList, { DescriptionListItem } from "veneer/DescriptionList";
import VeneerGrid from "veneer/Grid";
import ImageCarousel from "veneer/ImageCarousel";
import Stack from "veneer/Stack";
import { DownloadIcon } from "veneer/SvgIcon";
import styled from "veneer/styled";
import { Colors } from "veneer/Theme";
import Typography from "veneer/Typography";

import { STABLE, TESTING } from "cortex/ui/src/fleet/constant/appVersionType";
import {
  Application,
  ApplicationVersionField,
  UserParam,
} from "cortex/ui/src/fleet/view/application/model";
import { useAppDispatch, useAppSelector } from "cortex/ui/src/hooks";
import { createPromiseApiAction } from "cortex/ui/src/redux-store/api-sagas";
import {
  EDIT_APPLICATION,
  applicationsSelector,
} from "cortex/ui/src/redux-store/applications";
import {
  applicationsStoresLoadedSelector,
  getScreenshots,
  icon,
  name,
} from "cortex/ui/src/redux-store/applications/selectors";
import { getAuthUser } from "cortex/ui/src/redux-store/auth/selectors";
import * as Users from "cortex/ui/src/redux-store/users/selectors";
import { Header } from "cortex/ui/src/ui/header";
import { NavLoader } from "cortex/ui/src/ui/loader";
import Notification from "cortex/ui/src/ui/notification";
import { formatDateTimeLocal } from "cortex/ui/src/ui/table/formats";
import { getBreadcrumbs } from "cortex/ui/src/util/breadcrumbs";
import { sortByDisplayNameAsc } from "cortex/ui/src/util/sortByProp";
import { Container, Panel } from "cortex/ui/src/view/layout";

import ConfirmModal from "./ConfirmModal";

const Grid = styled(VeneerGrid)({
  display: "flex",
});

function getApplicationName(app: Application | null, type: string) {
  if (!app) {
    return undefined;
  }
  const appName = name(app);
  return type === TESTING ? `[TESTING] ${appName}` : appName;
}

function getApplicationDesc(app: Application | null, type: string) {
  if (!app) {
    return undefined;
  }
  const version =
    type === TESTING ? app.testing_version.version : app.stable_version.version;
  return `${name(app)} @${version}`;
}

function renderBreadcrumbs(application: Application | null) {
  if (!application) {
    return undefined;
  }
  const isInstalled = application.is_installed || application.is_own;

  return getBreadcrumbs([
    {
      label: isInstalled ? "Installed Apps" : "App Store",
      target: isInstalled ? "/applications" : "/applications/store",
    },
  ]);
}

export default function ApplicationDetails() {
  const { id, mode } = useParams<{ id: string; mode?: string }>();
  const applicationType = mode === "testing" ? TESTING : STABLE;
  const appById = useAppSelector(applicationsSelector);
  const application: Application | null = useMemo(
    () => (id ? (appById[Number(id)] ?? null) : null),
    [appById, id],
  );
  const user = useAppSelector(getAuthUser);
  const userIsAdmin = Users.isAdmin(user);
  const storesLoaded = useAppSelector(applicationsStoresLoadedSelector);
  const appVersion =
    applicationType === STABLE
      ? application?.stable_version
      : application?.testing_version;
  const userParams = appVersion?.user_params ?? [];
  const title = getApplicationName(application, applicationType);
  const desc = getApplicationDesc(application, applicationType);

  const VersionSwitchButton = () => {
    if (!application) return null;
    const { stable_version: stable, testing_version: testing } = application;
    if (!(stable && testing)) return null;

    if (applicationType === STABLE) {
      return (
        <Link
          style={{ alignItems: "center", display: "flex" }}
          to={`/applications/${application.id}/testing`}
        >
          <Button variant="outlined">View Testing Version</Button>
        </Link>
      );
    }
    return (
      <Link
        style={{ alignItems: "center", display: "flex" }}
        to={`/applications/${application.id}`}
      >
        <Button variant="outlined">View Stable Version</Button>
      </Link>
    );
  };

  return (
    <Container
      breadcrumbs={renderBreadcrumbs(application)}
      desc={desc}
      hideBox
      title={title}
      toolbox={<VersionSwitchButton />}
    >
      <View
        application={application}
        applicationType={applicationType}
        appVersion={appVersion}
        storesLoaded={storesLoaded}
        userIsAdmin={userIsAdmin}
        userParams={userParams}
      />
    </Container>
  );
}

interface BoldLabelProps {
  children: ReactNode;
  variant?: "body2" | "subtitle1";
}

function BoldLabel({ children, variant }: BoldLabelProps) {
  return (
    <Typography color={Colors.gray1000} fontWeight={600} variant={variant}>
      {children}
    </Typography>
  );
}

interface BadgeProps {
  application: Application;
  applicationType: string;
}

function Badges({ application, applicationType }: BadgeProps) {
  const chips: ReactNode[] = [];
  if (application.type === "APPLICATION") {
    chips.push(
      <JollyRancherChip color="orange" key="type" label="Application" />,
    );
  }
  chips.push(
    <JollyRancherChip
      color={application.private ? "gray" : "green"}
      key="visibility"
      label={application.private ? "Private" : "Public"}
    />,
  );
  if (application.required)
    chips.push(
      <JollyRancherChip color="red" key="required" label="Required" />,
    );
  chips.push(
    <JollyRancherChip
      color={application.is_installed || application.is_own ? "blue" : "gray"}
      key="install"
      label={
        application.is_installed || application.is_own
          ? "Installed"
          : "Not Installed"
      }
    />,
  );
  chips.push(
    <JollyRancherChip
      color={applicationType === STABLE ? "teal" : "gray"}
      key="version"
      label={applicationType === STABLE ? "Stable" : "Testing"}
    />,
  );
  return (
    <Stack direction="row" gap={1}>
      {chips}
    </Stack>
  );
}

interface ScreenshotProps {
  application: Application | null;
  applicationType: ApplicationVersionField;
}

function Screenshots({ application, applicationType }: ScreenshotProps) {
  if (!application) {
    return null;
  }
  const shots = getScreenshots(application, applicationType);
  if (shots.length === 0) return null;
  const imageObjects = shots.map((src: string) => ({
    image_url: src,
  }));
  return (
    <Panel borderRadius={2} title="Screenshots">
      <ImageCarousel autoPlay={true} images={imageObjects} transition="swipe" />
    </Panel>
  );
}

interface ParametersProps {
  application: Application | null;
  userParams?: ReadonlyArray<UserParam>;
}

function Parameters({ application, userParams }: ParametersProps) {
  if (!application) {
    return null;
  }
  if (!userParams) {
    return null;
  }

  const requiredParams = userParams
    .filter((p: any) => p.required)
    .sort(sortByDisplayNameAsc);
  const optionalParams = userParams
    .filter((p: any) => !p.required)
    .sort(sortByDisplayNameAsc);

  return (
    <Panel borderRadius={2} spacing={2} title="Configuration Parameters">
      <Header title="Required Parameters" />
      {requiredParams.length === 0 ? (
        <Typography variant="body2">
          This application doesn't have any required parameters.
        </Typography>
      ) : (
        <>
          {requiredParams.map((param) => {
            const { description, display_name, name } = param;
            return (
              <Stack key={name} spacing={0.5}>
                <BoldLabel variant="subtitle1">{display_name}</BoldLabel>
                <Typography color={Colors.gray1000} variant="body2">
                  {description}
                </Typography>
              </Stack>
            );
          })}
        </>
      )}

      <Header title="Optional Parameters" />
      {optionalParams.length === 0 ? (
        <Typography variant="body2">
          This application doesn't have any optional parameters.
        </Typography>
      ) : (
        <>
          {optionalParams.map((param) => {
            const { default_value, description, name, display_name } = param;
            const defaultValue = JSON.stringify(default_value);
            return (
              <Stack key={name} spacing={0.5}>
                <BoldLabel variant="subtitle1">{display_name}</BoldLabel>
                <Typography color={Colors.gray1000} variant="body2">
                  {description}
                </Typography>
                <Typography color={Colors.gray1000} variant="body2">
                  {`Default value: ${defaultValue}`}
                </Typography>
              </Stack>
            );
          })}
        </>
      )}
    </Panel>
  );
}

interface ViewProps {
  appVersion?: any;
  application: Application | null;
  applicationType: ApplicationVersionField;
  storesLoaded: boolean;
  userIsAdmin?: boolean;
  userParams?: ReadonlyArray<UserParam>;
}

function View({
  appVersion,
  application,
  applicationType,
  storesLoaded,
  userIsAdmin,
  userParams,
}: ViewProps) {
  if (!storesLoaded) {
    return <NavLoader.Proxy />;
  }
  if (!application) {
    return <Redirect to="/404" />;
  }

  const appIcon = icon(application);

  return (
    <Stack gap={2}>
      <Panel
        borderRadius={2}
        title="Details"
        toolbox={
          <Badges application={application} applicationType={applicationType} />
        }
      >
        <Stack direction="row" gap={2}>
          <Box width={250}>
            <img alt="App icon" src={appIcon} width="100%" />
          </Box>
          <AppDetails
            application={application}
            applicationType={applicationType}
            appVersion={appVersion}
          />
        </Stack>
        <Controls
          application={application}
          applicationType={applicationType}
          userIsAdmin={userIsAdmin}
        />
      </Panel>
      <Grid container gap={2}>
        <Grid flex={1} item>
          <Parameters application={application} userParams={userParams} />
        </Grid>
        <Grid flex={1} item>
          <Screenshots
            application={application}
            applicationType={applicationType}
          />
        </Grid>
      </Grid>
    </Stack>
  );
}

interface AppDetailsProps {
  appVersion: any;
  application: Application;
  applicationType: string;
}

function AppDetails({
  appVersion,
  application,
  applicationType,
}: AppDetailsProps) {
  if (!application) {
    return null;
  }
  const appDownloadUrl = appVersion ? appVersion.download_url : "";
  const { author, description, homepage, email } = application;

  return (
    <DescriptionList>
      <DescriptionListItem label="Description">
        {description}
      </DescriptionListItem>
      <DescriptionListItem label="Author">{author}</DescriptionListItem>
      <DescriptionListItem label="Homepage">
        <a href={homepage} rel="noopener noreferrer" target="_blank">
          {homepage}
        </a>
      </DescriptionListItem>
      <DescriptionListItem label="Support">{email}</DescriptionListItem>
      <AppDates
        application={application}
        applicationType={applicationType}
        appVersion={appVersion}
      />
      <DescriptionListItem label="Download">
        <a href={appDownloadUrl} rel="noopener noreferrer" target="_blank">
          <DownloadIcon size="small" />
        </a>
      </DescriptionListItem>
    </DescriptionList>
  );
}

interface AppDatesProps {
  appVersion: any;
  application: Application | null;
  applicationType: string;
}

function AppDates({ appVersion, application, applicationType }: AppDatesProps) {
  if (!application) {
    return null;
  }
  return (
    <>
      <DescriptionListItem label="Uploaded">
        {formatDateTimeLocal(appVersion.upload_date)}
      </DescriptionListItem>
      {applicationType === STABLE && (
        <DescriptionListItem label="Stable Since">
          {formatDateTimeLocal(appVersion.stable_since)}
        </DescriptionListItem>
      )}
    </>
  );
}

interface ControlsProps {
  application: Application | null;
  applicationType: string;
  userIsAdmin?: boolean;
}

function Controls({
  application,
  applicationType,
  userIsAdmin,
}: ControlsProps) {
  if (!userIsAdmin || !application) return null;
  const dispatch = useAppDispatch();
  const [errorMessage, setErrorMessage] = useState("");
  const [modals, setModals] = useState({
    makeStable: false,
    makePrivate: false,
    makePublic: false,
    switchVersion: false,
  });
  const [selectedVersion, setSelectedVersion] = useState<{
    value: string | number;
    label: string;
  } | null>(null);

  const {
    is_installed: isInstalled,
    is_own: isOwn,
    private: isPrivate,
    required,
    stable_version: stableApp,
    testing_version: testingApp,
  } = application;
  const stableV = stableApp ? stableApp.version : null;
  const testingV = testingApp ? testingApp.version : null;

  let mainBtn: ReactNode = null;
  let rollbackBtn: ReactNode = null;

  if (!isOwn && required) {
    return null;
  }

  const versionOptions = useMemo(() => {
    if (!application) return [];
    const currentStableId = application.stable_version
      ? application.stable_version.id
      : null;
    const currentTestingId = application.testing_version
      ? application.testing_version.id
      : null;

    const versions = application.versions || [];
    return versions
      .filter(
        (version) =>
          version.id !== currentStableId && version.id !== currentTestingId,
      )
      .map((version) => ({
        value: version.id,
        label: `${version.version} - ${formatDateTimeLocal(version.upload_date)}`,
      }))
      .sort((a: any, b: any) => (a.value < b.value ? 1 : -1));
  }, [application]);

  const editApp = useCallback(
    (serviceArgs: {
      id: number | string;
      operation: string;
      status: boolean;
      versionId?: string | number | null;
    }) => {
      const { id, operation, status, versionId = null } = serviceArgs;
      return createPromiseApiAction(dispatch, EDIT_APPLICATION, [
        id,
        operation,
        status,
        versionId,
      ]).catch(() => {
        setErrorMessage(
          "Failed to update the application. Please try again later.",
        );
      });
    },
    [dispatch],
  );

  const SwitchVersionModal = () => {
    if (!application) return null;
    const commit = async () => {
      if (!selectedVersion?.value) {
        setErrorMessage("Please select a version to switch to.");
        return;
      }

      const op =
        applicationType === TESTING
          ? "switch_testing_version"
          : "switch_stable_version";

      try {
        await editApp({
          id: application.id,
          operation: op,
          status: true,
          versionId: selectedVersion.value,
        });
      } catch {
        console.error("Failed to switch application version");
      } finally {
        cancel();
      }
    };

    const cancel = () => {
      setModals((m) => ({ ...m, switchVersion: false }));
      setSelectedVersion(null);
    };

    return !modals.switchVersion || !application ? null : (
      <ConfirmModal
        commitDisabled={!selectedVersion}
        commitLabel="Switch"
        onClose={cancel}
        onCommit={commit}
        open
      >
        <AutocompleteSelectInput
          disableClearable
          inputProps={{
            helperText:
              "Please select the application version you want to switch to.",
            label: "Select Version",
          }}
          onChange={(_, value) => setSelectedVersion(value)}
          options={versionOptions}
          size="small"
          value={selectedVersion ?? undefined}
        />
      </ConfirmModal>
    );
  };

  const MakeStableModal = () =>
    !modals.makeStable || !application ? null : (
      <ConfirmModal
        commitLabel="Mark As Stable"
        onClose={() => setModals((m) => ({ ...m, makeStable: false }))}
        onCommit={() =>
          editApp({
            id: application.id,
            operation: "stable",
            status: true,
          }).then(() => setModals((m) => ({ ...m, makeStable: false })))
        }
        open
      >
        This operation is irreversible. All of the users depending on your
        application will start using this version. Please make sure you test
        your application extensively before marking it as stable.
      </ConfirmModal>
    );

  const MakePrivateModal = () =>
    !modals.makePrivate || !application ? null : (
      <ConfirmModal
        commitLabel="Make Private"
        onClose={() => setModals((m) => ({ ...m, makePrivate: false }))}
        onCommit={() =>
          editApp({
            id: application.id,
            operation: "public",
            status: false,
          }).then(() => setModals((m) => ({ ...m, makePrivate: false })))
        }
        open
      >
        This operation will remove your application from the app store. <br />
        Cortex users will not be able to use your application any more.
      </ConfirmModal>
    );

  const MakePublicModal = () =>
    !modals.makePublic || !application ? null : (
      <ConfirmModal
        commitLabel="Make Public"
        onClose={() => setModals((m) => ({ ...m, makePublic: false }))}
        onCommit={() =>
          editApp({
            id: application.id,
            operation: "public",
            status: true,
          }).then(() => setModals((m) => ({ ...m, makePublic: false })))
        }
        open
      >
        This operation will make your application available on the app store.
        Any Cortex user will be able to use your application.
      </ConfirmModal>
    );

  if (stableV === testingV && applicationType === TESTING) {
    rollbackBtn = (
      <Button
        color="warning"
        onClick={() => {
          setSelectedVersion(versionOptions[0] ?? null);
          setModals((m) => ({ ...m, switchVersion: true }));
        }}
        size="small"
      >
        Change Testing Version
      </Button>
    );
    return (
      <Stack gap={1} mt={2}>
        {rollbackBtn}
        {errorMessage && <Notification error={errorMessage} />}
        <SwitchVersionModal />
      </Stack>
    );
  }

  if (isOwn) {
    if (applicationType === TESTING) {
      mainBtn = (
        <Button
          color="primary"
          onClick={() => setModals((m) => ({ ...m, makeStable: true }))}
          size="small"
        >
          Make Stable
        </Button>
      );
    } else if (isPrivate) {
      mainBtn = (
        <Button
          color="primary"
          onClick={() => setModals((m) => ({ ...m, makePublic: true }))}
          size="small"
        >
          Make Public
        </Button>
      );
    } else if (!required) {
      mainBtn = (
        <Button
          color="secondary"
          onClick={() => setModals((m) => ({ ...m, makePrivate: true }))}
          size="small"
        >
          Make Private
        </Button>
      );
    }

    if (versionOptions.length > 0) {
      const versionType = applicationType === TESTING ? "Testing" : "Stable";
      rollbackBtn = (
        <Button
          color="warning"
          onClick={() => {
            setSelectedVersion(versionOptions[0] ?? null);
            setModals((m) => ({ ...m, switchVersion: true }));
          }}
          size="small"
        >
          {`Change ${versionType} Version`}
        </Button>
      );
    }
  } else if (isInstalled) {
    mainBtn = (
      <Button
        color="secondary"
        onClick={() =>
          editApp({
            id: application.id,
            operation: "install",
            status: false,
          })
        }
        size="small"
      >
        Uninstall
      </Button>
    );
  } else {
    mainBtn = (
      <Button
        color="primary"
        onClick={() =>
          editApp({
            id: application.id,
            operation: "install",
            status: true,
          })
        }
        size="small"
      >
        Install
      </Button>
    );
  }

  return (
    <Stack gap={1} mt={2}>
      <Stack direction="row" gap={1}>
        {mainBtn}
        {rollbackBtn}
      </Stack>
      {errorMessage && <Notification error={errorMessage} />}
      <MakeStableModal />
      <MakePrivateModal />
      <MakePublicModal />
      <SwitchVersionModal />
    </Stack>
  );
}
